import EventEmitter from "eventemitter3";
import log from "loglevel";
import {formatObj} from "./logging";
import {DataChannelMessage} from "./messages";
import {getIceCandidatePairs} from "./stats";

const INITIAL_CALL_TIMEOUT = 500
const FAIL_RECONNECT_TIMEOUT = 3000
const UPDATE_OUT_MEDIA_STREAMS_TIMEOUT = 3000
const CHECKER_INTERVAl = 10000
const MESSAGE_SENDER_INTERVAL = 3000

export enum P2PConnectionDirection {
    send = 'send',
    recv = 'recv'
}

export interface P2PMediaStreamEvent {
    name: string,
    mediaStream: MediaStream
}

export interface DescriptionEvent {
    description: RTCSessionDescription
    streams: { [key: string]: string }
}

export class P2PConnection extends EventEmitter {
    public readonly connectionId: string
    private readonly _clientId: string
    private readonly _direction: P2PConnectionDirection
    private readonly _logger: log.Logger;
    private _pc!: RTCPeerConnection;
    private _dataChannel: RTCDataChannel | null = null
    private _establishConnectionCheckTimer: number | null = null
    private _streamsChecker: number | null = null
    private readonly _outMediaStreams: { [key: string]: MediaStream }
    private _inMediaStreamNames: { [key: string]: string } = {}
    private _messageSender: number | null = null
    private _messagesQueue: DataChannelMessage[] = []

    constructor(connectionId: string, clientId: string, direction: P2PConnectionDirection, logger: log.Logger, configuration?: RTCConfiguration, outMediaStreams?: { [key: string]: MediaStream }) {
        super()
        this.connectionId = connectionId
        this._clientId = clientId
        this._direction = direction
        this._logger = logger
        this._outMediaStreams = outMediaStreams || {}
        this._createRTCPeerConnection(configuration)
        setTimeout(() => this._call(), INITIAL_CALL_TIMEOUT)
        setTimeout(() => this._updateOutMediaStreams(), UPDATE_OUT_MEDIA_STREAMS_TIMEOUT)
        this._startMessageSender()
    }

    public async handleDescription(description: RTCSessionDescription, streams: { [key: string]: string }): Promise<void> {
        try {
            if (description.type === 'offer') {
                this._inMediaStreamNames = streams

                await this._pc.setRemoteDescription(description)
                const answer = await this._pc.createAnswer()
                await this._pc.setLocalDescription(answer)
                this.emit(
                    'description',
                    {
                        description: answer,
                        streams: this._outStreamNames
                    }
                )
            }

            if (description.type === 'answer') {
                await this._pc.setRemoteDescription(description)
            }
        } catch (err) {
            this._logger.error(formatObj({
                clientId: this._clientId,
                error: err
            }))
        }
    }

    public async addIceCandidate(candidate: RTCIceCandidate): Promise<void> {
        try {
            await this._pc.addIceCandidate(candidate)
        } catch (err) {
            if (this._direction === P2PConnectionDirection.send)
                this._logger.error(formatObj({
                    clientId: this._clientId,
                    error: err
                }))
        }
    }

    public addStream(name: string, mediaStream: MediaStream): void {
        this._outMediaStreams[name] = mediaStream
        this._updateOutMediaStreams()
    }

    public removeStream(name: string): void {
        delete this._outMediaStreams[name]
        this._updateOutMediaStreams()
    }

    public sendMessage(data: DataChannelMessage): void {
        this._messagesQueue.push(data)
    }

    public stop(): void {
        this._stopEstablishConnectionCheckTimer()
        this._stopStreamsChecker()
        this._stopMessageSender()
        this._inMediaStreamNames = {}
        this._dataChannel?.close()
        this._pc.close()
    }

    private _call(): void {
        if (this._direction === P2PConnectionDirection.recv) {
            this.emit('call')
            this._startEstablishConnectionCheckTimer()
        }
    }

    private _updateOutMediaStreams(): void {
        const expectedTracks: MediaStreamTrack[] = []
        const actualTracks = this._pc.getSenders().map(sender => sender.track)

        Object.values(this._outMediaStreams).forEach(mediaStream =>
            mediaStream.getTracks().forEach(mediaTrack => {
                expectedTracks.push(mediaTrack)
                if (actualTracks.indexOf(mediaTrack) < 0) {
                    try {
                        this._pc.addTrack(mediaTrack, mediaStream)
                    } catch (err) {
                        this._logger.error(formatObj({
                            clientId: this._clientId,
                            error: err
                        }))
                    }
                }

            })
        )

        this._pc.getSenders()
            .filter(sender => sender.track && expectedTracks.indexOf(sender.track) < 0)
            .forEach(sender => this._pc.removeTrack(sender))
    }

    private get _outStreamNames(): { [key: string]: string } {
        const streams: { [key: string]: string } = {}
        for (const [name, mediaStream] of Object.entries(this._outMediaStreams)) {
            streams[mediaStream.id] = name
        }
        return streams
    }

    private _createRTCPeerConnection(configuration?: RTCConfiguration) {
        const pc = new RTCPeerConnection(configuration)
        let dataChannel: RTCDataChannel | null = null

        pc.onicecandidate = ev => {
            this.emit('iceCandidate', ev)
        }

        pc.addEventListener('connectionstatechange', () => {
            if (pc.connectionState === 'connected') {
                this.emit('connected')

                getIceCandidatePairs(pc)
                    .then(pairs => pairs
                        .filter(pair => pair.state === 'succeeded')
                        .forEach(pair => this._logger.info('ICE PAIR:', formatObj({
                            clientId: this._clientId,
                            state: pair.state,
                            localCandidate: formatObj(pair.localCandidate),
                            remoteCandidate: formatObj(pair.remoteCandidate)
                        })))
                    )
                    .catch((reason: string) =>
                        this._logger.error(formatObj({
                            clientId: this._clientId,
                            reason
                        }))
                    )
            }
        })

        if (this._direction === P2PConnectionDirection.send) {
            dataChannel = pc.createDataChannel('messages')
            dataChannel.onmessage = ev => this.emit('message', ev.data)

            pc.onnegotiationneeded = async () => {
                this._logger.info('NEGOTIATIONNEEDED', formatObj({
                    clientId: this._clientId,
                    direction: this._direction
                }))
                const offer = await pc.createOffer()
                await pc.setLocalDescription(offer)

                this.emit(
                    'description',
                    {
                        description: this._pc.localDescription,
                        streams: this._outStreamNames
                    }
                )
            }
        }

        if (this._direction === P2PConnectionDirection.recv) {
            pc.ondatachannel = ev => {
                this._dataChannel = ev.channel
                this._dataChannel.onmessage = ev => this.emit('message', JSON.parse(ev.data as string))
            }

            pc.ontrack = (ev: RTCTrackEvent) => {
                this._logger.info('NEW TRACK', formatObj({
                    clientId: this._clientId,
                    direction: this._direction,
                    track: this._inMediaStreamNames[ev.streams[0].id]
                }))
                this.emit('mediaStream', {
                    name: this._inMediaStreamNames[ev.streams[0].id],
                    mediaStream: ev.streams[0]
                })
            }

            pc.oniceconnectionstatechange = () => {
                this._logger.info('ICE CONNECTION STATE', formatObj({
                    clientId: this._clientId,
                    direction: this._direction,
                    state: pc.iceConnectionState
                }))
                if (pc.iceConnectionState === 'failed') {
                    this._recreateRTCPeerConnection()
                }
            }

            pc.addEventListener("connectionstatechange", () => {
                this._logger.info('CONNECTION STATE', formatObj({
                    clientId: this._clientId,
                    direction: this._direction,
                    state: pc.connectionState
                }))
                switch (pc.connectionState) {
                    case "connected":
                        this._startStreamsChecker()
                        break
                    case "failed":
                        setTimeout(() => pc.connectionState === "failed" && this._recreateRTCPeerConnection(), FAIL_RECONNECT_TIMEOUT)
                        break
                    case "disconnected":
                        setTimeout(() => pc.connectionState === "disconnected" && this._recreateRTCPeerConnection(), FAIL_RECONNECT_TIMEOUT)
                        break
                    default:
                        break
                }
            })

            pc.onsignalingstatechange = () => {
                this._logger.info('SIGNALING STATE', formatObj({
                    clientId: this._clientId,
                    direction: this._direction,
                    state: pc.signalingState
                }))
            }
        }
        this._pc = pc
        this._dataChannel = dataChannel
    }


    private _recreateRTCPeerConnection(): void {
        this.stop()
        this._createRTCPeerConnection(this._pc.getConfiguration())
        if (this._direction === P2PConnectionDirection.recv) {
            this._call()
        }
    }

    private _startEstablishConnectionCheckTimer(): void {
        this._stopEstablishConnectionCheckTimer()
        this._establishConnectionCheckTimer = window.setTimeout(() => {
            switch (this._pc.connectionState) {
                case 'new':
                    this._call()
                    break
                case 'connecting':
                    this._recreateRTCPeerConnection()
            }
        }, CHECKER_INTERVAl)
    }

    private _stopEstablishConnectionCheckTimer(): void {
        if (this._establishConnectionCheckTimer) {
            window.clearTimeout(this._establishConnectionCheckTimer)
        }
    }

    private _startStreamsChecker(): void {
        if (this._direction !== P2PConnectionDirection.recv)
            return
        this._stopStreamsChecker()

        this._streamsChecker = window.setInterval(() => {
            if (this._pc.connectionState !== "connected") {
                return this._recreateRTCPeerConnection()
            }
        }, CHECKER_INTERVAl)

    }

    private _stopStreamsChecker(): void {
        if (this._streamsChecker)
            window.clearInterval(this._streamsChecker)
    }

    private _startMessageSender(): void {
        this._stopMessageSender()

        this._messageSender = window.setInterval(() => {
            if (this._dataChannel?.readyState === 'open') {
                let message = this._messagesQueue.shift()
                while (message !== undefined) {
                    this._dataChannel.send(JSON.stringify(message))
                    message = this._messagesQueue.shift()
                }
            }
        }, MESSAGE_SENDER_INTERVAL)

    }

    private _stopMessageSender(): void {
        if (this._messageSender)
            window.clearInterval(this._messageSender)
    }
}

